import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [activeLink, setActiveLink] = useState('');

    useEffect(() => {
        const sectionIds = ['hero', 'hizmetlerimiz', 'neden-biz', 'nasil-calisir', 'iletisim'];

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveLink(entry.target.id);
                    }
                });
            },
            { rootMargin: '-40% 0px -60% 0px' }
        );

        if (location.pathname === '/') {
            sectionIds.forEach((id) => {
                const element = document.getElementById(id);
                if (element) {
                    observer.observe(element);
                }
            });
        } else {
            sectionIds.forEach((id) => {
                const element = document.getElementById(id);
                if (element) {
                    observer.unobserve(element);
                }
            });
            if (location.pathname === '/calismalarimiz') {
                setActiveLink('calismalarimiz');
            } else {
                setActiveLink('');
            }
        }

        return () => {
            sectionIds.forEach((id) => {
                const element = document.getElementById(id);
                if (element) {
                    observer.unobserve(element);
                }
            });
        };
    }, [location.pathname]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
    };

    const scrollToSection = (sectionId) => {
        setActiveLink(sectionId);
        // Eğer ana sayfada değilsek, önce ana sayfaya git
        if (location.pathname !== '/') {
            navigate('/');
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } else {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
        setIsMenuOpen(false);
    };

    const handleWorksClick = () => {
        setActiveLink('calismalarimiz');
        navigate('/calismalarimiz');
        setIsMenuOpen(false);
    };

    return (
        <>
            <style jsx>{`
                @keyframes slideInFromRight {
                    from {
                        transform: translateX(100px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                .animate-slide-in {
                    animation: slideInFromRight 1.2s ease-out;
                }
            `}</style>

            <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        {/* Logo */}
                        <div className="flex items-center">
                            <h1 className="text-2xl font-bold text-primary-600 cursor-pointer flex" onClick={() => scrollToSection('hero')}>
                                <span>Diji</span>
                                <span className="animate-slide-in text-black">Vite</span>
                            </h1>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center space-x-8">
                            <button onClick={() => scrollToSection('hizmetlerimiz')} className={`transition-colors font-medium ${activeLink === 'hizmetlerimiz' ? 'text-red-600' : 'text-dark-700'} hover:text-primary-600`}>Hizmetlerimiz</button>
                            <button onClick={handleWorksClick} className={`transition-colors font-medium ${activeLink === 'calismalarimiz' ? 'text-red-600' : 'text-dark-700'} hover:text-primary-600`}>Çalışmalarımız</button>
                            <button onClick={() => scrollToSection('neden-biz')} className={`transition-colors font-medium ${activeLink === 'neden-biz' ? 'text-red-600' : 'text-dark-700'} hover:text-primary-600`}>Neden Biz?</button>
                            <button onClick={() => scrollToSection('nasil-calisir')} className={`transition-colors font-medium ${activeLink === 'nasil-calisir' ? 'text-red-600' : 'text-dark-700'} hover:text-primary-600`}>Nasıl Çalışır</button>
                        </nav>

                        {/* Right Side - Dark Mode & CTA */}
                        <div className="hidden md:flex items-center space-x-4">


                            {/* CTA Button */}
                            <button
                                onClick={() => scrollToSection('iletisim')}
                                className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                                İletişime Geç
                            </button>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={toggleMenu}
                            className="md:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                            <svg className="w-6 h-6 text-dark-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>

                    {/* Mobile Navigation */}
                    {isMenuOpen && (
                        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-gray-100 backdrop-blur-sm">
                            <div className="px-4 py-6">
                                <div className="flex flex-col space-y-1">
                                    <button
                                        onClick={() => scrollToSection('hero')}
                                        className={`text-left py-3 px-4 rounded-lg group transition-all duration-200 font-medium ${activeLink === 'hero' ? 'text-red-600 bg-red-50' : 'text-dark-700 hover:text-primary-600 hover:bg-primary-50'}`}
                                    >
                                        <span className="flex items-center">
                                            <span className={`w-2 h-2 rounded-full mr-3 transition-opacity duration-200 ${activeLink === 'hero' ? 'bg-red-400 opacity-100' : 'bg-primary-400 opacity-0 group-hover:opacity-100'}`}></span>
                                            Ana Sayfa
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => scrollToSection('hizmetlerimiz')}
                                        className={`text-left py-3 px-4 rounded-lg group transition-all duration-200 font-medium ${activeLink === 'hizmetlerimiz' ? 'text-red-600 bg-red-50' : 'text-dark-700 hover:text-primary-600 hover:bg-primary-50'}`}
                                    >
                                        <span className="flex items-center">
                                            <span className={`w-2 h-2 rounded-full mr-3 transition-opacity duration-200 ${activeLink === 'hizmetlerimiz' ? 'bg-red-400 opacity-100' : 'bg-primary-400 opacity-0 group-hover:opacity-100'}`}></span>
                                            Hizmetlerimiz
                                        </span>
                                    </button>
                                    <button
                                        onClick={handleWorksClick}
                                        className={`text-left py-3 px-4 rounded-lg group transition-all duration-200 font-medium ${activeLink === 'calismalarimiz' ? 'text-red-600 bg-red-50' : 'text-dark-700 hover:text-primary-600 hover:bg-primary-50'}`}
                                    >
                                        <span className="flex items-center">
                                            <span className={`w-2 h-2 rounded-full mr-3 transition-opacity duration-200 ${activeLink === 'calismalarimiz' ? 'bg-red-400 opacity-100' : 'bg-primary-400 opacity-0 group-hover:opacity-100'}`}></span>
                                            Çalışmalarımız
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => scrollToSection('neden-biz')}
                                        className={`text-left py-3 px-4 rounded-lg group transition-all duration-200 font-medium ${activeLink === 'neden-biz' ? 'text-red-600 bg-red-50' : 'text-dark-700 hover:text-primary-600 hover:bg-primary-50'}`}
                                    >
                                        <span className="flex items-center">
                                            <span className={`w-2 h-2 rounded-full mr-3 transition-opacity duration-200 ${activeLink === 'neden-biz' ? 'bg-red-400 opacity-100' : 'bg-primary-400 opacity-0 group-hover:opacity-100'}`}></span>
                                            Neden Biz?
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => scrollToSection('nasil-calisir')}
                                        className={`text-left py-3 px-4 rounded-lg group transition-all duration-200 font-medium ${activeLink === 'nasil-calisir' ? 'text-red-600 bg-red-50' : 'text-dark-700 hover:text-primary-600 hover:bg-primary-50'}`}
                                    >
                                        <span className="flex items-center">
                                            <span className={`w-2 h-2 rounded-full mr-3 transition-opacity duration-200 ${activeLink === 'nasil-calisir' ? 'bg-red-400 opacity-100' : 'bg-primary-400 opacity-0 group-hover:opacity-100'}`}></span>
                                            Nasıl Çalışır
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => scrollToSection('iletisim')}
                                        className={`text-left py-3 px-4 rounded-lg group transition-all duration-200 font-medium ${activeLink === 'iletisim' ? 'text-red-600 bg-red-50' : 'text-dark-700 hover:text-primary-600 hover:bg-primary-50'}`}
                                    >
                                        <span className="flex items-center">
                                            <span className={`w-2 h-2 rounded-full mr-3 transition-opacity duration-200 ${activeLink === 'iletisim' ? 'bg-red-400 opacity-100' : 'bg-primary-400 opacity-0 group-hover:opacity-100'}`}></span>
                                            İletişim
                                        </span>
                                    </button>
                                </div>

                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <button
                                        onClick={() => scrollToSection('iletisim')}
                                        className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-6 rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
                                    >
                                        <span>İletişime Geç</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>
        </>
    );
};

export default Header;